import React, { useEffect, useState } from 'react';
import { merchantSettlementsAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import {
  Landmark, CheckCircle2, Clock, XCircle, AlertCircle,
  ChevronLeft, ChevronRight, Loader2, Filter, CalendarDays,
  TrendingDown, IndianRupee, ReceiptText,
} from 'lucide-react';

/* ── design tokens ── */
const C = {
  bg:          '#f4f4f9',
  surface:     '#FFFFFF',
  border:      '#e8e8f1',
  borderSoft:  '#f0f0f6',
  ink:         '#14142b',
  body:        '#4a4a6a',
  muted:       '#6e6e86',
  primary:     '#7c3aed',
  primarySoft: '#f1ebff',
  primaryTint: '#ddd6fe',
  success:     '#10B981',
  successSoft: '#D1FAE5',
  warning:     '#F59E0B',
  warnSoft:    '#FEF3C7',
  danger:      '#EF4444',
  dangerSoft:  '#FEE2E2',
  radius:      14,
  radiusSm:    8,
};

const fmtINR = (n) =>
  '₹ ' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
};

const fmtShortDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
};

/* ── status badge ── */
const STATUS_MAP = {
  pending:    { bg: C.warnSoft,    color: '#92400E', Icon: Clock         },
  processing: { bg: C.primarySoft, color: C.primary, Icon: AlertCircle   },
  completed:  { bg: C.successSoft, color: '#065F46', Icon: CheckCircle2  },
  failed:     { bg: C.dangerSoft,  color: '#991B1B', Icon: XCircle       },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_MAP[status?.toLowerCase()] || STATUS_MAP.pending;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      fontSize: 11, fontWeight: 700, padding: '3px 10px',
      borderRadius: 20, background: s.bg, color: s.color,
    }}>
      <s.Icon size={10} strokeWidth={2.5} />
      {(status || 'pending').charAt(0).toUpperCase() + (status || 'pending').slice(1)}
    </span>
  );
};

const TH = ['Settlement ID', 'Date', 'Txns', 'Amount', 'Status', 'Bank Ref'];

const filterSel = {
  padding: '8px 12px', fontSize: 12, color: C.ink,
  background: C.bg, border: `1px solid ${C.border}`,
  borderRadius: C.radiusSm, outline: 'none',
};

/* ── Pending Today Card ── */
const PendingCard = ({ pending, loadingPending }) => {
  if (loadingPending) {
    return (
      <div style={{
        background: C.surface, border: `1px solid ${C.border}`,
        borderRadius: C.radius, padding: '28px', marginBottom: 18,
        display: 'flex', justifyContent: 'center',
        boxShadow: '0 1px 3px rgba(20,20,43,.04)',
      }}>
        <Loader2 size={22} color={C.primary} style={{ animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  if (!pending) return null;

  const cycleLabel = pending.settlement_cycle
    ? pending.settlement_cycle.replace(/^T(\d)$/, 'T+$1')
    : 'T+3';

  return (
    <div style={{
      background: 'linear-gradient(135deg, #f1ebff 0%, #F0FDF4 100%)',
      border: `1.5px solid ${C.primaryTint}`,
      borderRadius: C.radius, padding: '20px 24px', marginBottom: 18,
      boxShadow: '0 2px 8px rgba(124,58,237,.08)',
    }}>
      {/* heading row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10,
            background: C.primarySoft, color: C.primary,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <CalendarDays size={17} strokeWidth={1.8} />
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 800, color: C.ink }}>Today's Pending Settlement</div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 1 }}>
              Based on transactions from&nbsp;
              <strong style={{ color: C.body }}>{fmtShortDate(pending.settle_date)}</strong>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <span style={{
            background: C.primarySoft, color: C.primary,
            borderRadius: 999, padding: '4px 14px', fontSize: 12, fontWeight: 800,
          }}>{cycleLabel}</span>
          <span style={{
            background: C.successSoft, color: C.success,
            borderRadius: 999, padding: '4px 14px', fontSize: 12, fontWeight: 800,
          }}>{pending.settlement_percentage}% fee</span>
        </div>
      </div>

      {/* KPI row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
        {[
          {
            label: 'Gross Amount',
            value: fmtINR(pending.gross_amount),
            sub: `${pending.txn_count} transactions`,
            Icon: IndianRupee,
            iconBg: C.primarySoft,
            iconColor: C.primary,
            valueColor: C.ink,
          },
          {
            label: 'Settlement Fee',
            value: `- ${fmtINR(pending.fee_amount)}`,
            sub: `${pending.settlement_percentage}% of gross`,
            Icon: TrendingDown,
            iconBg: C.dangerSoft,
            iconColor: C.danger,
            valueColor: C.danger,
          },
          {
            label: 'Net Payable',
            value: fmtINR(pending.net_amount),
            sub: 'Will be credited today',
            Icon: ReceiptText,
            iconBg: C.successSoft,
            iconColor: C.success,
            valueColor: C.success,
          },
        ].map(({ label, value, sub, Icon, iconBg, iconColor, valueColor }) => (
          <div key={label} style={{
            background: C.surface, borderRadius: C.radiusSm,
            border: `1px solid ${C.borderSoft}`, padding: '14px 16px',
            display: 'flex', alignItems: 'flex-start', gap: 12,
          }}>
            <div style={{
              width: 34, height: 34, borderRadius: 8,
              background: iconBg, color: iconColor,
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <Icon size={15} strokeWidth={2} />
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 3 }}>{label}</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: valueColor, letterSpacing: '-.01em' }}>{value}</div>
              <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{sub}</div>
            </div>
          </div>
        ))}
      </div>

      {pending.txn_count === 0 && (
        <div style={{ marginTop: 14, padding: '10px 14px', background: C.warnSoft, borderRadius: C.radiusSm, fontSize: 13, color: '#92400E', fontWeight: 600 }}>
          No successful transactions found for {fmtShortDate(pending.settle_date)} — nothing to settle today.
        </div>
      )}
    </div>
  );
};

const UserSettlements = () => {
  const { user } = useAuth();
  const [page,           setPage]           = useState(1);
  const [status,         setStatus]         = useState('');
  const [data,           setData]           = useState({ data: [], last_page: 1 });
  const [loading,        setLoading]        = useState(true);
  const [pending,        setPending]        = useState(null);
  const [loadingPending, setLoadingPending] = useState(true);

  const settlementCycle = user?.settlement_cycle ?? 't3';
  const settlementPct   = user?.settlement_percentage ?? '3.00';
  const cycleLabel      = settlementCycle === 't1' ? 'T+1' : settlementCycle === 't2' ? 'T+2' : 'T+3';

  const loadPending = async () => {
    setLoadingPending(true);
    try {
      const { data: resp } = await merchantSettlementsAPI.pendingToday();
      setPending(resp?.data || null);
    } catch {
      /* non-critical */
    } finally {
      setLoadingPending(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = { page };
      if (status) params.status = status;
      const { data: resp } = await merchantSettlementsAPI.list(params);
      setData(resp?.data || { data: [], last_page: 1 });
    } catch {
      toast.error('Could not load settlements');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadPending(); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, status]);

  const rows     = data.data     || [];
  const lastPage = data.last_page || 1;

  return (
    <div style={{ background: C.bg, minHeight: '100vh', padding: '28px 28px 40px' }}>

      {/* ── page header ── */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10,
            background: C.primarySoft, color: C.primary,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Landmark size={17} strokeWidth={1.8} />
          </div>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: C.ink, margin: 0 }}>Settlements</h2>
        </div>
        <p style={{ fontSize: 13, color: C.muted, margin: 0, paddingLeft: 46 }}>
          Settlement batches credited to your registered bank account.
        </p>
      </div>

      {/* ── Settlement config info bar ── */}
      <div style={{
        background: C.surface, border: `1px solid ${C.border}`,
        borderRadius: C.radius, padding: '14px 20px', marginBottom: 18,
        display: 'flex', alignItems: 'center', gap: 24,
        boxShadow: '0 1px 3px rgba(20,20,43,.04)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '.06em' }}>Settlement Cycle</span>
          <span style={{
            background: C.primarySoft, color: C.primary,
            borderRadius: 999, padding: '3px 12px', fontSize: 12, fontWeight: 800,
          }}>{cycleLabel}</span>
        </div>
        <div style={{ width: 1, height: 20, background: C.border }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '.06em' }}>Settlement %</span>
          <span style={{
            background: C.successSoft, color: C.success,
            borderRadius: 999, padding: '3px 12px', fontSize: 12, fontWeight: 800,
          }}>{settlementPct}%</span>
        </div>
      </div>

      {/* ── Pending today card ── */}
      <PendingCard pending={pending} loadingPending={loadingPending} />

      {/* ── filter bar ── */}
      <div style={{
        background: C.surface, border: `1.5px solid ${C.border}`,
        borderRadius: C.radiusSm, padding: '10px 16px', marginBottom: 18,
        display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
      }}>
        {[
          { label: 'All',        val: '',             color: C.primary },
          { label: 'Pending',    val: 'pending',      color: C.warning },
          { label: 'Processing', val: 'processing',   color: C.primary },
          { label: 'Completed',  val: 'completed',    color: C.success },
          { label: 'Failed',     val: 'failed',       color: C.danger  },
        ].map(t => (
          <button key={t.val} onClick={() => { setPage(1); setStatus(t.val); }}
            style={{ height: 34, padding: '0 14px', borderRadius: 999, border: `1.5px solid ${status === t.val ? t.color : C.border}`, background: status === t.val ? t.color : 'transparent', color: status === t.val ? '#fff' : C.body, fontWeight: 600, fontSize: 12, cursor: 'pointer', transition: 'all .15s', whiteSpace: 'nowrap', fontFamily: 'inherit' }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── table card ── */}
      <div style={{
        background: C.surface, border: `1px solid ${C.border}`,
        borderRadius: C.radius, padding: '20px 24px',
        boxShadow: '0 1px 3px rgba(20,20,43,.04)',
      }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
            <Loader2 size={30} color={C.primary} style={{ animation: 'spin 1s linear infinite' }} />
          </div>
        ) : rows.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <Landmark size={40} color={C.border} strokeWidth={1} />
            <p style={{ marginTop: 14, color: C.muted, fontSize: 14 }}>No settlements yet</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${C.borderSoft}` }}>
                  {TH.map(h => (
                    <th key={h} style={{
                      padding: '9px 14px', textAlign: 'left',
                      fontSize: 11, fontWeight: 700, color: C.muted,
                      textTransform: 'uppercase', letterSpacing: '.06em', whiteSpace: 'nowrap',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map(s => (
                  <tr key={s.id}
                    style={{ borderBottom: `1px solid ${C.borderSoft}`, transition: 'background .1s' }}
                    onMouseEnter={e => e.currentTarget.style.background = C.bg}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <td style={{ padding: '12px 14px', fontFamily: 'monospace', fontSize: 12, color: C.body, whiteSpace: 'nowrap' }}>
                      {s.settlement_id}
                    </td>
                    <td style={{ padding: '12px 14px', color: C.muted, fontSize: 12, whiteSpace: 'nowrap' }}>
                      {fmtDate(s.created_at)}
                    </td>
                    <td style={{ padding: '12px 14px', fontWeight: 600, color: C.body }}>
                      {s.txn_count ?? '—'}
                    </td>
                    <td style={{ padding: '12px 14px', fontWeight: 700, color: C.ink, whiteSpace: 'nowrap' }}>
                      {fmtINR(s.gross_amount)}
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <StatusBadge status={s.status} />
                    </td>
                    <td style={{ padding: '12px 14px', fontFamily: 'monospace', fontSize: 12, color: C.muted }}>
                      {s.bank_ref || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ── pagination ── */}
        {lastPage > 1 && (
          <div style={{
            display: 'flex', justifyContent: 'flex-end', alignItems: 'center',
            marginTop: 18, paddingTop: 14, borderTop: `1px solid ${C.borderSoft}`, gap: 4,
          }}>
            <button
              onClick={() => setPage(p => p - 1)} disabled={page === 1}
              style={{
                width: 32, height: 32, borderRadius: 8, border: `1px solid ${C.border}`,
                background: C.surface, cursor: page === 1 ? 'not-allowed' : 'pointer',
                color: page === 1 ? C.muted : C.body,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <ChevronLeft size={14} />
            </button>

            {Array.from({ length: lastPage }, (_, i) => i + 1).map(p => (
              <button key={p} onClick={() => setPage(p)}
                style={{
                  width: 32, height: 32, borderRadius: 8, fontSize: 13,
                  border: `1px solid ${p === page ? C.primary : C.border}`,
                  background: p === page ? C.primary : C.surface,
                  color: p === page ? '#fff' : C.body,
                  fontWeight: p === page ? 700 : 400,
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >{p}</button>
            ))}

            <button
              onClick={() => setPage(p => p + 1)} disabled={page === lastPage}
              style={{
                width: 32, height: 32, borderRadius: 8, border: `1px solid ${C.border}`,
                background: C.surface, cursor: page === lastPage ? 'not-allowed' : 'pointer',
                color: page === lastPage ? C.muted : C.body,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <ChevronRight size={14} />
            </button>
          </div>
        )}
      </div>

      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
};

export default UserSettlements;
